import { ServiceRepresentation } from '../../../../../../components/dataRepresentation/ServiceRepresentation';
import { CustomFormattedMessage } from '../../../../../../i18n/i18n';
import { useServiceConfigurationFormContext } from '../ServiceConfigurationForm';
import {
  PaymentMethodState,
  PaymentPartnerType,
  ServiceType,
} from '../../../../../../codegen/vehicles';
import Select from '@rio-cloud/rio-uikit/lib/es/Select';
import { ServiceCardRepresentation } from '../../../../../../components/dataRepresentation/ServiceCardRepresentation';
import { MaskedCardNumber } from '../../../../../../components/MaskedCardNumber';
import { FormField } from '../../../../../../components/form/FormField';
import { Controller } from 'react-hook-form';
import isEmpty from 'lodash/isEmpty';
import { DIALOG_DATA_COLOR } from '../ServiceConfigurationDialog';
import { UserManualLink } from '../../../../../../components/dialog/userManualDialog/UserManualLink';
import { userManualDialogChapters } from '../../../../../../state/dialogs/chapters';
import { PaymentMethod } from '../../../../../../models';

const userManualChapters = {
  [ServiceType.Fueling]: userManualDialogChapters.mobileFueling,
  [ServiceType.Parking]: userManualDialogChapters.parking,
  [ServiceType.Washing]: userManualDialogChapters.washing,
  [ServiceType.Breakdown]: userManualDialogChapters.repairMaintenance,
  [ServiceType.Charging]: userManualDialogChapters.charging,
  [ServiceType.Other]: userManualDialogChapters.otherOperatingCosts,
};

type ServiceExpanderProps = {
  serviceType: ServiceType;
};

export const ServiceExpander = ({ serviceType }: ServiceExpanderProps) => {
  const { control, getPaymentMethods } = useServiceConfigurationFormContext();

  const serviceCardOptions = [
    {
      id: '',
      label: (
        <div className={'display-flex justify-content-end'}>
          <CustomFormattedMessage id={'onboarding.dialog.serviceCards.none'} />
        </div>
      ),
    },
  ];

  const paymentMethodIsAvailableForService = (paymentMethod: PaymentMethod) => {
    // Charge&Go Cards can only be used for Charging, but not for other services
    return (
      serviceType === ServiceType.Charging ||
      paymentMethod.paymentPartner !== PaymentPartnerType.ManChargeAndGoPayment
    );
  };

  getPaymentMethods()
    .filter((pm) => pm.state === PaymentMethodState.Active)
    .filter((pm) => paymentMethodIsAvailableForService(pm))
    .forEach((pm) => {
      serviceCardOptions.push({
        id: pm.id,
        label: (
          <div className={'display-flex justify-content-between'}>
            <ServiceCardRepresentation paymentPartner={pm.paymentPartner} />
            <div className={'margin-left-20'}>
              <MaskedCardNumber lastFourDigits={pm.lastFourDigits} />
            </div>
          </div>
        ),
      });
    });

  return (
    <div
      className={'margin-top-20'}
      data-testid={`onboarding-dialog-body-service-${serviceType}`}
    >
      <div className={'panel panel-default margin-bottom-0'}>
        <div className="panel-body padding-bottom-0">
          <div className={'display-flex align-items-center'}>
            <div className={`${DIALOG_DATA_COLOR} text-size-20`}>
              <ServiceRepresentation
                serviceType={serviceType}
                representationSize="large"
              />
            </div>
            <div className={'margin-left-5'}>
              <UserManualLink
                chapter={userManualChapters[serviceType]}
                dataTestId={`user-manual-link-service-${serviceType}`}
              />
            </div>
          </div>
        </div>
        <div
          className={'margin-10'}
          data-testid={'onboarding-dialog-body-serviceCardTypeSection'}
        >
          <Controller
            name={`serviceActivations.${serviceType}.paymentMethodAssignments`}
            render={({ field: { onChange, value } }) => (
              <FormField>
                <Select
                  aria-labelledby="card-type-label"
                  placeholder={
                    <CustomFormattedMessage
                      id={
                        'onboarding.dialog.paymentMethodDetails.selectCardTypePlaceholder'
                      }
                    />
                  }
                  options={serviceCardOptions}
                  onChange={(e) => {
                    onChange(
                      isEmpty(e?.id)
                        ? []
                        : [
                            {
                              paymentMethodId: e?.id,
                            },
                          ],
                    );
                  }}
                  value={isEmpty(value) ? [] : [value[0].paymentMethodId]}
                />
              </FormField>
            )}
            control={control}
            rules={{
              required: true,
            }}
          />
        </div>
      </div>
    </div>
  );
};
